import { useState, useCallback, useEffect, useMemo } from 'react';

interface SiteData {
  captain_id: string;
  learn_url?: string;
  fleet_name?: string;
  aws_account?: string;
  [key: string]: any;
}

interface SiteConsistencyValidationResult {
  isValid: boolean;
  requiredFleet?: string;
  requiredAccount?: string;
  incompatibleSites?: string[];
}

interface UseSiteSelectionOptions {
  maxSelection?: number;
  initialSelectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  isDialogOpen?: boolean;
  allLoadedSites?: SiteData[];
  requiredFleetId?: string;
  requiredAccountId?: string;
  sitesCache?: Map<string, SiteData>;
}

export const useSiteSelection = ({
  maxSelection = 60,
  initialSelectedIds = new Set<string>(),
  onSelectionChange,
  isDialogOpen = false,
  allLoadedSites = [],
  requiredFleetId,
  requiredAccountId,
  sitesCache,
}: UseSiteSelectionOptions = {}) => {
  const [tempSelectedSiteIds, setTempSelectedSiteIds] = useState<Set<string>>(new Set());
  const [initialSelectedSiteIds, setInitialSelectedSiteIds] = useState<Set<string>>(new Set());
  const [siteConsistencyError, setSiteConsistencyError] =
    useState<SiteConsistencyValidationResult | null>(null);

  useEffect(() => {
    if (isDialogOpen) {
      const newSelectedIds = new Set(initialSelectedIds);
      setTempSelectedSiteIds(newSelectedIds);
      setInitialSelectedSiteIds(new Set(initialSelectedIds));
      setSiteConsistencyError(null);
    }
  }, [isDialogOpen, initialSelectedIds]);

  const validateSiteConsistency = useCallback(
    (selectedSiteIds: Set<string>): SiteConsistencyValidationResult => {
      if (selectedSiteIds.size <= 1) {
        return { isValid: true };
      }

      const selectedSites = Array.from(selectedSiteIds)
        .map((id) => {
          const cachedSite = sitesCache?.get(id);
          if (cachedSite) return cachedSite;
          return allLoadedSites.find((site) => site.captain_id === id);
        })
        .filter((site): site is SiteData => !!site);

      if (selectedSites.length === 0) {
        return { isValid: true };
      }

      const hasRequiredFleet = requiredFleetId && requiredFleetId.trim() !== '';
      const hasRequiredAccount = requiredAccountId && requiredAccountId.trim() !== '';

      if (hasRequiredFleet || hasRequiredAccount) {
        const incompatibleSites = selectedSites
          .filter((site) => {
            const fleetMismatch = hasRequiredFleet && site.fleet_name !== requiredFleetId;
            const accountMismatch = hasRequiredAccount && site.aws_account !== requiredAccountId;
            return fleetMismatch || accountMismatch;
          })
          .map((site) => site.captain_id);

        return {
          isValid: incompatibleSites.length === 0,
          requiredFleet: hasRequiredFleet ? requiredFleetId : undefined,
          requiredAccount: hasRequiredAccount ? requiredAccountId : undefined,
          incompatibleSites,
        };
      }

      const firstSite = selectedSites[0];
      const requiredFleet = firstSite.fleet_name;
      const requiredAccount = firstSite.aws_account;

      const incompatibleSites = selectedSites
        .filter((site) => site.fleet_name !== requiredFleet || site.aws_account !== requiredAccount)
        .map((site) => site.captain_id);

      return {
        isValid: incompatibleSites.length === 0,
        requiredFleet,
        requiredAccount,
        incompatibleSites,
      };
    },
    [allLoadedSites, requiredFleetId, requiredAccountId, sitesCache],
  );

  const toggleSite = useCallback(
    (siteId: string) => {
      setTempSelectedSiteIds((prev) => {
        const next = new Set(prev);
        if (next.has(siteId)) {
          next.delete(siteId);
        } else if (next.size < maxSelection) {
          next.add(siteId);
        }
        return next;
      });
    },
    [maxSelection],
  );

  const handleSelectAll = useCallback(
    (availableSites: SiteData[], checked: boolean) => {
      if (checked) {
        const sitesToSelect = availableSites.slice(0, maxSelection).map((site) => site.captain_id);
        setTempSelectedSiteIds(new Set(sitesToSelect));
      } else {
        setTempSelectedSiteIds(new Set());
      }
    },
    [maxSelection],
  );

  const confirmSelection = useCallback(() => {
    const validation = validateSiteConsistency(tempSelectedSiteIds);

    if (!validation.isValid) {
      setSiteConsistencyError(validation);
      return false;
    }

    onSelectionChange?.(new Set(tempSelectedSiteIds));
    setInitialSelectedSiteIds(new Set(tempSelectedSiteIds));
    setSiteConsistencyError(null);
    return true;
  }, [tempSelectedSiteIds, validateSiteConsistency, onSelectionChange]);

  const cancelSelection = useCallback(() => {
    setTempSelectedSiteIds(new Set(initialSelectedSiteIds));
    setSiteConsistencyError(null);
  }, [initialSelectedSiteIds]);

  const clearSiteConsistencyError = useCallback(() => {
    setSiteConsistencyError(null);
  }, []);

  const selectedCount = tempSelectedSiteIds.size;
  const maxReached = selectedCount >= maxSelection;
  const hasSelection = selectedCount > 0;

  const hasChanges = useMemo(() => {
    if (tempSelectedSiteIds.size !== initialSelectedSiteIds.size) {
      return true;
    }
    return Array.from(tempSelectedSiteIds).some((id) => !initialSelectedSiteIds.has(id));
  }, [tempSelectedSiteIds, initialSelectedSiteIds]);

  const isSelected = useCallback(
    (siteId: string) => tempSelectedSiteIds.has(siteId),
    [tempSelectedSiteIds],
  );

  const canSelect = useCallback(
    (siteId: string) => !tempSelectedSiteIds.has(siteId) && tempSelectedSiteIds.size < maxSelection,
    [tempSelectedSiteIds, maxSelection],
  );

  return {
    selectedSiteIds: tempSelectedSiteIds,
    initialSelectedSiteIds,
    selectedCount,
    maxSelection,
    hasSelection,
    maxReached,
    hasChanges,
    siteConsistencyError,
    toggleSite,
    handleSelectAll,
    confirmSelection,
    cancelSelection,
    clearSiteConsistencyError,
    isSelected,
    canSelect,
    validateSiteConsistency,
  };
};
